import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useEffect, type ReactNode } from 'react'

import { useCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'
import { useDrawerGuard } from '@/Ship/Components/UI/AppDrawer/DrawerGuardContext'
import { isFormDirty } from '@/Ship/Helpers/formDirty'

import { ServiceInfo } from '../ServiceInfo/ServiceInfo'

/** Поля картки, потрібні блоку «Службова інформація» */
const AUDIT_KEYS = ['id', 'uuid', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy'] as const

/**
 * Службові поля зі значень картки для ServiceInfo — лише ключі, що є у відповіді: відсутній
 * `createdBy` (сутність без аудиту автора) ≠ порожній. Для шаблонів, що показують блок самі
 * (напр. у вкладці «Інформація»), коли ресурс ховає його через hideServiceInfoInView.
 */
export const auditValuesOf = (formValues: Record<string, any> | null | undefined): Record<string, unknown> | null =>
    formValues
        ? Object.fromEntries(AUDIT_KEYS.filter((key) => key in formValues).map((key) => [key, toJS(formValues[key])]))
        : null

interface DetailShellProps {
    children: ReactNode
}

/** Вміст картки: шаблон форми + «Службова інформація»; реєструє guard незбережених змін */
const DetailBody = observer(({ crudContext, children }: DetailShellProps & { crudContext: any }) => {
    const guardApi = useDrawerGuard()
    const formContext = crudContext.formContext

    useEffect(() => {
        if (!guardApi || !formContext) return undefined
        guardApi.setGuard(() => {
            if (!formContext.isEditable) return false
            try {
                const fieldNames = Object.keys(crudContext.resource?.fields ?? {})
                return isFormDirty(toJS(formContext.values), toJS(formContext.initialValues), fieldNames)
            } catch (error) {
                // Порівняння не має блокувати закриття картки
                console.warn('[DetailShell] Не вдалося порівняти значення форми', error)
                return false
            }
        })
        return () => guardApi.setGuard(null)
    }, [guardApi, formContext, crudContext])

    // ServiceInfo отримує лише службові поля: підписка на решту значень форми зайва
    const auditValues = auditValuesOf(formContext?.values)

    // Ресурс може прибрати блок у перегляді (resource.hideServiceInfoInView), коли шаблон
    // сам показує потрібне — напр. задача: ID і дата у зведенні. У редагуванні блок лишається.
    // resource.hideServiceInfo прибирає блок в усіх режимах картки (напр. заявка)
    const resource = crudContext.resource
    const hidden =
        resource?.hideServiceInfo === true ||
        (formContext?.isEditable === false && resource?.hideServiceInfoInView === true)

    return (
        <>
            {children}
            {!hidden && <ServiceInfo values={auditValues} />}
        </>
    )
})

/**
 * Обгортка шаблону картки (templateWrapper у CrudResourceConfig): для detail-контексту
 * додає «Службову інформацію» знизу і перевірку незбережених змін для AppDrawer;
 * для списків і всього іншого — прозора.
 */
export const DetailShell = ({ children }: DetailShellProps) => {
    const crudContext = useCrudResourceContext() as any
    if (!crudContext || crudContext.contextType !== 'detail') return <>{children}</>
    return <DetailBody crudContext={crudContext}>{children}</DetailBody>
}
